// Session + scope store.
//
// Single zustand store that owns the current AuthUser + Scope. Consumers read
// it via the narrow selectors `useCurrentUser` / `useCurrentScope` to keep
// re-render surfaces small.
//
// Fetch contract:
//   - GET /auth/me → { user, scope } on 200
//   - 401 clears local state (user logged out server-side)
//   - network errors keep stale data and surface `error` for banners
//
// TODO [WEEK-5]: hook real workspace switching via `X-Workspace-Id` once the
// workspace picker lands.

import { create } from 'zustand';

import type { AuthUser } from '@govard/auth-adapter';
import type { Scope } from '@govard/tenant-core';

import { ApiError, apiGet } from '../api-client/index.js';

export interface WorkspaceSettingsState {
  workspaceId: string;
  featureFlags: Record<string, boolean>;
}

interface MeResponse {
  user: AuthUser;
  // The backend also returns `session`, but the web shell only needs
  // user + scope. Keep the parsed shape loose.
  session?: unknown;
  scope: Scope;
  workspaceSettings: WorkspaceSettingsState;
}

interface ScopeStoreState {
  user: AuthUser | null;
  scope: Scope | null;
  workspaceSettings: WorkspaceSettingsState | null;
  isLoading: boolean;
  error: Error | null;
  fetchMe: () => Promise<void>;
  clear: () => void;
}

export const useScopeStore = create<ScopeStoreState>((set, get) => ({
  user: null,
  scope: null,
  workspaceSettings: null,
  isLoading: false,
  error: null,

  fetchMe: async () => {
    if (get().isLoading) {
      return;
    }
    set({ isLoading: true, error: null });
    try {
      const me = await apiGet<MeResponse>('/auth/me');
      set({ user: me.user, scope: me.scope, workspaceSettings: me.workspaceSettings, isLoading: false, error: null });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        // Unauthorized = not logged in. Clear any stale state.
        set({ user: null, scope: null, workspaceSettings: null, isLoading: false, error: null });
        return;
      }
      // Other errors (network, 5xx) — keep stale data, surface the error.
      const error = err instanceof Error ? err : new Error('unknown error fetching /auth/me');
      set({ isLoading: false, error });
    }
  },

  clear: () => {
    set({ user: null, scope: null, workspaceSettings: null, error: null });
  },
}));

export function useCurrentUser(): AuthUser | null {
  return useScopeStore((state) => state.user);
}

export function useCurrentScope(): Scope | null {
  return useScopeStore((state) => state.scope);
}

export function useCurrentWorkspaceSettings(): WorkspaceSettingsState | null {
  return useScopeStore((state) => state.workspaceSettings);
}

export function useScopeLoading(): boolean {
  return useScopeStore((state) => state.isLoading);
}

export function useScopeError(): Error | null {
  return useScopeStore((state) => state.error);
}
